<template>
  <UPopover
    v-model:open="isOpen"
    :content="filterContent"
    :ui="filterUi"
  >
    <button
      type="button"
      class="bg-primary/10 border border-primary/30 cursor-pointer dark:bg-primary/20 dark:border-primary/40 dark:hover:bg-primary/25 flex focus:outline-none hover:bg-primary/20 items-center px-4 py-2 relative rounded-l-lg space-x-2 text-primary transition-colors z-(--z-index-ds-raised)"
    >
      <!-- On the search page the address shows on the button when present; store pages never show it. -->
      <template v-if="streetAddress && !isStorePage">
        <MapPinIcon class="shrink-0 size-4" />

        <span class="font-medium text-sm">{{ streetAddress }}</span>

        <span class="text-sm">•</span>
      </template>

      <component
        :is="selectedOrderTypeIcon"
        class="shrink-0 size-4"
      />

      <span class="font-medium text-sm">{{ selectedOrderTypeTitle }}</span>

      <ChevronDownIcon class="shrink-0 size-4" />
    </button>

    <template #content>
      <div class="p-6">
        <!-- Order Type -->
        <div class="mb-6">
          <h3 class="dark:text-content-inverse font-semibold mb-2 text-content-level-900 text-lg">
            {{ $t('commerce.locationFilter.selectOrderType') }}
          </h3>

          <div class="gap-x-4 grid grid-cols-2">
            <RadioOptionCard
              v-for="type in orderTypes"
              :key="type.value"
              v-model="pendingOrderType"
              :accessible-name="type.title"
              v-bind="orderTypeLabelAttrs(type.description)"
              name="orderType"
              :value="type.value"
              :title="type.title"
              :description="type.description"
              title-size="md"
            />
          </div>
        </div>

        <!-- The search page always shows the address section; store pages never do. -->
        <div
          v-show="!isStorePage"
          class="mb-4"
        >
          <h3 class="dark:text-content-inverse font-semibold mb-2 text-content-level-900 text-lg">
            {{ $t('commerce.locationFilter.location') }}
          </h3>

          <div class="overflow-visible relative">
            <AddressSearchInput
              v-model="addressValue"
              :placeholder="$t('commerce.locationFilter.locationPlaceholder')"
              size="sm"
              type="no-address"
              @fallback-clear="pendingFallback.reset"
              @fallback-input="pendingFallback.set"
            />
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end space-x-3">
          <button
            v-if="currentLocation || currentOrderType !== OrderType.DINE_IN"
            type="button"
            class="bg-surface-canvas border border-border-level-300 cursor-pointer dark:bg-surface-clear dark:border-border-inverse/20 dark:hover:bg-surface-canvas/10 dark:text-content-level-200 font-medium hover:bg-surface-level-50 px-4 py-2 rounded-md text-content-level-700 text-sm"
            @click="handleClearClick"
          >
            {{ $t('commerce.locationFilter.clear') }}
          </button>

          <button
            type="button"
            class="bg-primary cursor-pointer font-medium hover:bg-primary/90 px-4 py-2 rounded-md text-content-inverse text-sm"
            @click="handleUpdateClick"
          >
            {{ $t('commerce.locationFilter.update') }}
          </button>
        </div>
      </div>
    </template>
  </UPopover>
</template>

<script setup lang="ts">
import UPopover from '@nuxt/ui/components/Popover.vue'
import {
  MapPinIcon,
  ChevronDownIcon,
  ShoppingBagIcon,
  BuildingStorefrontIcon
} from '@heroicons/vue/24/outline'
import AddressSearchInput from '~/components/AddressSearchInput.vue'
import RadioOptionCard from '~/design-system/molecules/RadioOptionCard/RadioOptionCard.molecule.vue'
import { useFallbackAddressText } from '~/composables/useFallbackAddressText'
import { useLocationFilterSearch } from '~/composables/useLocationFilterSearch'
import { SEARCH_ORDER_TYPES } from '~/constants/search'
import { computed, ref, watch } from 'vue'
import type { Component } from 'vue'
import type { AddressSearchValue } from '~/lib/geo-providers/interfaces'
import { OrderType } from '~/enums'
import { overlayContentClasses } from '~/utils/overlayContentClasses'

// Props
const props = withDefaults(
  defineProps<{
    /** Current location filter value. */
    currentLocation?: string
    /** Current order type filter value. */
    currentOrderType?: string
    /** Indicates the filter is rendered on a store page. */
    isStorePage?: boolean
  }>(),
  {
    currentLocation: '',
    currentOrderType: OrderType.DINE_IN,
    isStorePage: false
  }
)

const emit = defineEmits<{
  'update:location': [value: string]
  'update:orderType': [value: string]
  'update:coordinates': [value: Pick<AddressSearchValue, 'lat' | 'lng'> | null]
}>()

// aria-description isn't in Vue's shipped LabelHTMLAttributes type yet;
// it's still a valid, functional ARIA attribute that forwards via $attrs.
const orderTypeLabelAttrs = (description: string) => ({ 'aria-description': description }) as const

// Stores
const { applyAddressSelection, applyAddressText, clearLocation, refreshResults } =
  useLocationFilterSearch()

// Reactive data
const addressValue = ref<AddressSearchValue | null>(null)
const pendingLocation = ref('')
const pendingOrderType = ref('')
const pendingCoordinates = ref<Pick<AddressSearchValue, 'lat' | 'lng'> | null>(null)
const pendingBbox = ref<number[] | null>(null)
const pendingPlaceType = ref<string | null>(null)
const pendingFallback = useFallbackAddressText()

// Order types (Group Order / dine_in first as default)
const orderTypeIcons: Record<string, Component> = {
  [OrderType.DINE_IN]: BuildingStorefrontIcon,
  [OrderType.PICKUP]: ShoppingBagIcon
}

const orderTypes = SEARCH_ORDER_TYPES.map((type) => ({
  ...type,
  icon: orderTypeIcons[type.value] || BuildingStorefrontIcon
}))

// Computed properties
const streetAddress = computed(() => {
  if (!props.currentLocation) return ''

  // Extract just the street address (first part before comma)
  const parts = props.currentLocation.split(',')
  const firstPart = parts[0]?.trim()
  if (firstPart === undefined || firstPart === '') return props.currentLocation

  return firstPart
})

const selectedOrderTypeTitle = computed(() => {
  const orderType = orderTypes.find((type) => type.value === props.currentOrderType)
  return orderType?.title
})

const selectedOrderTypeIcon = computed(() => {
  const orderType = orderTypes.find((type) => type.value === props.currentOrderType)
  return orderType?.icon || BuildingStorefrontIcon
})

// Methods

// Distinguishes the display-only pre-fill in setInitialAddressValue() from a genuine
// user pick, so the watcher below doesn't mistake a fabricated placeholder for real
// pending coordinates (see AddressSearchInput.vue's clearingInternally for the same idiom).
const isDisplayOnlyUpdate = ref(false)

// Set initial address value from current location (for URL query restoration)
const setInitialAddressValue = () => {
  if ((props.currentLocation ?? '') === '' || addressValue.value !== null) return

  // Set the model value — AddressSearchInput reads it via watch/setValue
  isDisplayOnlyUpdate.value = true
  addressValue.value = {
    address: props.currentLocation,
    lat: 0,
    lng: 0
  }
}

// Watch address selection for pending state
watch(addressValue, (val) => {
  if (isDisplayOnlyUpdate.value) {
    isDisplayOnlyUpdate.value = false
    return
  }
  if (val !== null) {
    pendingLocation.value = val.address
    pendingCoordinates.value = { lat: val.lat, lng: val.lng }
    pendingBbox.value = val.bbox ?? null
    pendingPlaceType.value = val.placeType ?? null
  }
})

const isOpen = ref(false)

const filterContent = { align: 'center', sideOffset: 12 } as const

const filterUi = {
  content: [
    overlayContentClasses.frame,
    overlayContentClasses.panel,
    'max-w-lg overflow-visible text-sm/6 w-screen'
  ].join(' ')
} as const

const hydratePendingState = () => {
  pendingLocation.value = props.currentLocation
  pendingOrderType.value = props.currentOrderType
  pendingCoordinates.value = null
  pendingBbox.value = null
  pendingPlaceType.value = null
  pendingFallback.reset()
  setInitialAddressValue()
}

watch(isOpen, (open) => {
  if (open) hydratePendingState()
})

const commitPendingLocation = (): void => {
  const pendingCoords = pendingCoordinates.value
  const locationChanged = Boolean(pendingLocation.value && pendingCoords)

  if (pendingFallback.hasLocatableText.value) {
    applyAddressText(pendingFallback.text.value)
    emit('update:location', pendingFallback.text.value.trim())
    emit('update:coordinates', null)
  } else if (locationChanged && pendingCoords !== null) {
    applyAddressSelection({
      address: pendingLocation.value,
      bbox: pendingBbox.value,
      lat: pendingCoords.lat,
      lng: pendingCoords.lng,
      placeType: pendingPlaceType.value
    })
    emit('update:location', pendingLocation.value)
    emit('update:coordinates', pendingCoords)
  } else if (pendingLocation.value) {
    emit('update:location', pendingLocation.value)
  }
}

const handleUpdateClick = async () => {
  try {
    commitPendingLocation()

    if (pendingOrderType.value) {
      emit('update:orderType', pendingOrderType.value)
    }

    // No redirect needed - the watchers on search/store pages will handle updates
  } finally {
    isOpen.value = false
  }
}

const handleClearClick = async () => {
  // Check if there's a location query (meaning there is a location used)
  const hasLocationQuery = clearLocation()

  // Clear pending values
  pendingLocation.value = ''
  pendingCoordinates.value = null
  pendingBbox.value = null
  pendingPlaceType.value = null
  pendingFallback.reset()
  pendingOrderType.value = OrderType.DINE_IN
  addressValue.value = null

  // Emit cleared values
  emit('update:location', '')
  emit('update:coordinates', null)
  emit('update:orderType', OrderType.DINE_IN)

  // Clear the location parameter from the URL if there was a location query
  if (hasLocationQuery && typeof window !== 'undefined') {
    const url = new URL(window.location.href)
    url.searchParams.delete('location')
    // Update URL without page reload
    window.history.replaceState({}, '', url.toString())
  }

  if (!props.isStorePage) {
    await refreshResults()
  }

  isOpen.value = false
}

const openPopover = () => {
  isOpen.value = true
}

defineExpose({ openPopover })
</script>
